import { css, cx } from "@emotion/css";
import { ActionButton, ActionButtonType, Callout, PopoverBasicWithTrigger, tableBorder } from "@octopusdeploy/design-system-components";
import { PlusIcon } from "@octopusdeploy/design-system-icons";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import { borderWidth, fontWeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ChannelResource, EnvironmentV2Resource, FeedResource, GetTeamsLookupBffResponseTeam, GitRefResource, PackageReference, PlatformHubAccountSummary, ProcessTemplateParameterResource, ProcessTemplateResource, ProjectSummaryResource, PropertyValueResource, ScopeSpecification, ScopeValues, VariableSetResource, WorkerPoolResource, } from "@octopusdeploy/octopus-server-client";
import { isAccountControlType, ControlType, VariableType, getParametersOfType } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import { flatten } from "lodash";
import * as React from "react";
import { useMemo, useState, useRef } from "react";
import type { StepEditorEvent } from "~/analytics/Analytics";
import { useScopeWidth } from "~/areas/platformHub/processEditor/useScopeWidth";
import { baseCellHeightInRem, multipleValuesAndScopesIndicatorHeightInRem } from "~/areas/platformHub/processTemplates/ProcessTemplateParametersTab";
import type { ParameterValueRow, TemplateValueKey } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import { createTemplateValueKey, TemplateParameterScopeCell } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import type { FailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { isFailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import type { StoredAction } from "~/areas/projects/components/Process/types";
import type { LoadedLibraryVariableSets } from "~/areas/projects/components/Variables/AllVariables/SharedAllVariablesPage";
import ReadonlyInlineVariableValue from "~/areas/projects/components/Variables/TenantVariables/ReadonlyInlineVariableValue";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import { useProjectContext } from "~/areas/projects/context";
import MissingVariablesIcon from "~/areas/tenants/MissingVariablesIcon/MissingVariablesIcon";
import { isAccountType } from "~/areas/variables/isAccountType";
import { repository } from "~/clientInstance";
import type { ProjectSourceItems } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import ActionTemplateParameterInput from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import { createSummaryFor } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInputExpandableFormElement";
import { getLabel } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInputRenderer";
import { type DoBusyTask } from "~/components/DataBaseComponent/index";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import { IconButtonWithTooltip } from "~/components/IconButtonWithTooltip";
import Markdown from "~/components/Markdown";
import PopoverWhenFocused from "~/components/PopoverWhenFocused";
import convertPropertyValueResourceToString from "~/components/convertPropertyValueResourceToString";
import { FormSection, FormSectionHeading, Summary, Text } from "~/components/form/index";
import type { Options } from "~/primitiveComponents/form/Select/Options";
import selectOptionsToItems from "~/primitiveComponents/form/Select/Options";
import { getAllProjectActionTemplates } from "~/utils/LoadProjectVariables/loadProjectVariables";
import { stringifyActionProperty } from "~/utils/actionPropertyUtils";
import { JsonUtils } from "~/utils/jsonUtils";
interface ProcessTemplateActionParameterValuesProps {
    stepId: string;
    action: StoredAction;
    doBusyTask: DoBusyTask;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    processTemplate: ProcessTemplateResource;
    gitRefResource: GitRefResource | undefined;
    processVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    environments: EnvironmentV2Resource[];
    availableTargetTags: string[];
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    availableWorkerPools: WorkerPoolResource[];
    teams: GetTeamsLookupBffResponseTeam[];
    isTenanted: boolean;
    channels: ChannelResource[];
    projects: ProjectSummaryResource[];
    refreshVariables: () => Promise<void>;
}
interface ClearOldParameterValuesCalloutProps {
    onClearValues: () => void;
}
export function ClearOldParameterValuesCallout({ onClearValues }: ClearOldParameterValuesCalloutProps) {
    return (<Callout type={"information"} title={"Clear old parameter values"}>
            <div className={styles.calloutContainer}>
                <div>One or more parameters have been removed from the current version of the process template. We recommend clearing these values to avoid any unexpected behavior.</div>
                <div>
                    <ActionButton type={ActionButtonType.Primary} label={"Clear Removed Values"} onClick={onClearValues}/>
                </div>
            </div>
        </Callout>);
}
export function ProcessTemplateActionParameterValues({ stepId, action, doBusyTask, onParameterValueChanged, processTemplate, gitRefResource, processVariableSet, libraryVariableSets, environments, availableTargetTags, analyticsStepEditorDispatch, availableWorkerPools, isTenanted, teams, channels, projects, refreshVariables, }: ProcessTemplateActionParameterValuesProps) {
    const projectContext = useProjectContext();
    const processContext = useProcessContext();
    const actionNames: string[] = flatten(Object.values(processContext.state.model.actions.byId).map((a) => a.Name));
    const feeds = useFeedsFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();
    const [parameterWorkerPools, setParameterWorkerPools] = useState(availableWorkerPools);
    const platformHubAccountIds = useMemo(() => {
        const ids = processTemplate.Parameters.filter((p) => isAccountType(getVariableTypeFromDisplaySettings(p.DisplaySettings["Octopus.ControlType"]))).flatMap((p) => p.Values.map((v) => v.Value));
        return Array.from(new Set(ids)); //return distinct ids
    }, [processTemplate.Parameters]);
    const { result: platformHubAccountNames, error: platformHubAccountNamesError, isLoading, } = useInlineStatusQuery(() => repository.PlatformHubAccounts.getAccountNames(platformHubAccountIds), [platformHubAccountIds], "Get platform hub account names");
    const packageReferences: PackageReference[] = getParametersOfType(processTemplate.Parameters, ControlType.Package).map((packageParam) => {
        const existingPackage = action.Packages.find((existingPackage) => packageParam.Name === existingPackage.Name);
        return {
            Name: packageParam.Name,
            PackageId: "",
            FeedId: "",
            AcquisitionLocation: "",
            Properties: {
                PackageParameterName: packageParam.Name,
                SelectionMode: "deferred",
            },
            Id: "",
            Version: existingPackage?.Version,
        };
    });
    const sourceItems: ProjectSourceItems = {
        stepNames: actionNames,
        projectId: projectContext.state.model.Id,
        processIdentifier: processContext.state.processIdentifier,
        variableSet: processVariableSet,
        libraryVariableSets,
        refreshVariables,
        gitRef: gitRefResource,
        packages: {
            items: packageReferences,
            feeds: feeds,
            onRequestRefresh: async () => {
                await doBusyTask(async () => {
                    await refreshFeeds();
                });
            },
            setPackages: (packages: PackageReference[]) => {
                processContext.actions.setActionMetaProperties(action.Id, (prev) => ({ ...prev, Packages: packages }));
            },
        },
        availableTargetTags,
        environments,
        workerPools: {
            items: parameterWorkerPools,
            onRequestRefresh: () => doBusyTask(async () => {
                const summaries = await repository.WorkerPools.summary();
                setParameterWorkerPools(summaries.WorkerPoolSummaries.map((s) => s.WorkerPool));
            }),
        },
        teams,
        channels,
        projects,
        actionTemplates: getAllProjectActionTemplates(projectContext.state.model, isFailedPermissionCheck(libraryVariableSets) ? [] : libraryVariableSets.map((lvs) => lvs.libraryVariableSet)),
    };
    const parameterNamesInAction = Object.keys(action.Properties).filter((p) => !p.startsWith("Octopus"));
    const parameterNamesInProcessTemplate = new Set(processTemplate.Parameters.map((p) => p.Name));
    const parametersRemovedFromProcessTemplate = parameterNamesInAction.filter((p) => !parameterNamesInProcessTemplate.has(p));
    const visibleParameters = processTemplate.Parameters.filter((p) => shouldRenderParameter(p, action, availableWorkerPools, isTenanted));
    const parameterValueRows = getParameterValueRows(visibleParameters, action, sourceItems);
    const sortedParameterValueRows = [...parameterValueRows.filter((row) => isAccountControlType(getControlTypeFromParameterValueRow(row))), ...parameterValueRows.filter((row) => !isAccountControlType(getControlTypeFromParameterValueRow(row)))];
    const clearValues = () => {
        processContext.actions.removeActionProperties(action.Id, parametersRemovedFromProcessTemplate);
    };
    if (platformHubAccountNamesError) {
        return (<Callout type="danger" title="Process template account parameter issue">
                The Platform Hub account referenced by a template parameter is no longer available. Contact your Platform Hub administrator to resolve this issue.
            </Callout>);
    }
    return (<>
            {visibleParameters.length > 0 && !isLoading && platformHubAccountNames ? (<>
                    {parametersRemovedFromProcessTemplate.length > 0 ? <ClearOldParameterValuesCallout onClearValues={clearValues}/> : null}
                    <ProcessTemplateParameterValuesTable parameterValueRows={sortedParameterValueRows} availableScopeValues={{ ...defaultScopeValues, Environments: environments }} platformHubAccounts={platformHubAccountNames} stepId={stepId} sourceItems={sourceItems} action={action} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch}/>
                    {parametersRemovedFromProcessTemplate.length > 0 && (<>
                            <FormSectionHeading title="Old Parameter Values"/>
                            {parametersRemovedFromProcessTemplate.map((parameter) => (<FormSection key={parameter} title={<span className={styles.removedParameter}>{parameter}</span>} includeBorder={true} help={<span className={styles.removedParameter}>{stringifyActionProperty(action.Properties[parameter])}</span>}></FormSection>))}
                        </>)}
                </>) : null}
        </>);
}
interface ProcessTemplateParameterValuesTableProps {
    parameterValueRows: ParameterValueRow[];
    availableScopeValues: ScopeValues;
    platformHubAccounts: PlatformHubAccountSummary[];
    stepId: string;
    sourceItems: ProjectSourceItems;
    action: StoredAction;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
}
export function ProcessTemplateParameterValuesTable({ parameterValueRows, availableScopeValues, platformHubAccounts, stepId, sourceItems, action, onParameterValueChanged, doBusyTask, analyticsStepEditorDispatch, }: ProcessTemplateParameterValuesTableProps) {
    const { scopeHeaderRef, scopeWidth } = useScopeWidth();
    const [focusedValueKey, setFocusedValueKey] = useState<TemplateValueKey | null>(null);
    const nameColumnWidthAsPercentage = 26;
    const valueAndScopeColumnWidthAsPercentage = 37;
    const hideScopeColumn = parameterValueRows.filter((row) => isAccountControlType(getControlTypeFromParameterValueRow(row))).length === 0;
    return (<GroupedDataTable data={parameterValueRows} columns={[
            {
                title: "Name",
                render: (data) => data.ValueIndex === 0 ? (<div>
                                <div className={styles.parameterNameCellPrimaryHeader}>{data.Parameter.Label || data.Parameter.Name}</div>
                                {data.Parameter.Label && <div className={styles.parameterNameCellSecondaryHeader}>{data.Parameter.Name}</div>}
                                <div className={styles.parameterHelpText}>
                                    <Markdown markup={data.Parameter.HelpText} noMargins={true} mutedTextColor={true}/>
                                </div>
                            </div>) : null,
                width: `${nameColumnWidthAsPercentage}%`,
                isChild: (data) => data.ValueIndex !== 0,
                padding: space[12],
                collapseNullCellsUpwards: true,
                verticalAlign: "top",
            },
            {
                title: "Value",
                render: (data) => (<ProcessTemplateConsumerParameterValueCell data={data} stepId={stepId} sourceItems={sourceItems} action={action} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch} platformHubAccounts={platformHubAccounts} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey}/>),
                width: hideScopeColumn ? `${2 * valueAndScopeColumnWidthAsPercentage}%` : `${valueAndScopeColumnWidthAsPercentage}%`,
                padding: "0",
            },
            ...(hideScopeColumn
                ? []
                : [
                    {
                        title: "Scope",
                        render: (data: ParameterValueRow) => {
                            const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
                            const getScopeCellContents = () => {
                                if (!data.Value) {
                                    return null;
                                }
                                if (getUniqueAccountVariableNames(sourceItems).includes(data.Value.Value)) {
                                    return <div className={cx(styles.disabledText, styles.valueContainer)}>Defined by variable</div>;
                                }
                                return (<TemplateParameterScopeCell parameter={data.Parameter} value={data.Value} onChange={noOp} index={data.ValueIndex} isEditing={false} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} scopeValues={availableScopeValues} height={48} width={scopeWidth} isConsumer={true}/>);
                            };
                            return (<div>
                                          {showMultipleValuesAndScopesIndicators(controlType, data) && (<div className={styles.multipleValuesAndScopesIndicator}>
                                                  <div>Multiple scopes</div>
                                              </div>)}
                                          <div className={styles.scopeContainer}>{getScopeCellContents()}</div>
                                      </div>);
                        },
                        width: `${valueAndScopeColumnWidthAsPercentage}%`,
                        padding: "0",
                        ref: scopeHeaderRef,
                    },
                ]),
        ]} getRowKey={(data) => `${data.Parameter.Name}-${data.ValueIndex}`}/>);
}
interface ProcessTemplateParameterProps {
    parameter: ProcessTemplateParameterResource;
    stepId: string;
    sourceItems: ProjectSourceItems;
    action: StoredAction;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    autoFocus: boolean;
}
function ProcessTemplateParameter({ stepId, parameter, sourceItems, action, onParameterValueChanged, doBusyTask, analyticsStepEditorDispatch, autoFocus }: ProcessTemplateParameterProps) {
    const type = parameter.DisplaySettings["Octopus.ControlType"];
    const value = action.Properties[parameter.Name];
    const processErrorSelectors = useProcessErrorSelectors();
    const getStepFieldError = (value: string) => processErrorSelectors.getStepFieldError(stepId, value);
    return type ? (<div className={styles.parameterContainer}>
            <ActionTemplateParameterInput parameter={parameter} sourceItems={sourceItems} doBusyTask={doBusyTask} value={value} onChange={(value) => onParameterValueChanged(parameter.Name, value)} actionType={action.ActionType} error={getStepFieldError(parameter.Name)} analyticsStepEditorDispatch={analyticsStepEditorDispatch} autoFocus={autoFocus}/>
        </div>) : null;
}
export const shouldRenderParameter = (parameter: ProcessTemplateParameterResource, action: StoredAction, availableWorkerPools: WorkerPoolResource[], isTenanted: boolean) => {
    if (parameter.DisplaySettings["Octopus.ControlType"] === ControlType.WorkerPool && availableWorkerPools.length === 0) {
        return false;
    }
    if (parameter.DisplaySettings["Octopus.ControlType"] === ControlType.TenantTags) {
        const value = convertPropertyValueResourceToString(action.Properties[parameter.Name]);
        if (!isTenanted && StringHelper.isNullOrWhiteSpace(value)) {
            return false;
        }
        return true;
    }
    return true;
};
export const isAccountVariableType = (variableType: VariableType): boolean => variableType === VariableType.AmazonWebServicesAccount ||
    variableType === VariableType.AzureAccount ||
    variableType === VariableType.GoogleCloudAccount ||
    variableType === VariableType.UsernamePasswordAccount ||
    variableType === VariableType.GenericOidcAccount;
export interface ProjectSourceVariable {
    Name: string;
    Scope: ScopeSpecification;
    Type: VariableType;
}
export const getAllVariables = (sourceItems: ProjectSourceItems): ProjectSourceVariable[] => {
    const processVariables = isFailedPermissionCheck(sourceItems.variableSet) ? [] : sourceItems.variableSet.Variables;
    const libraryVariables = isFailedPermissionCheck(sourceItems.libraryVariableSets) ? [] : sourceItems.libraryVariableSets.flatMap((librarySet) => librarySet.variableSet.Variables);
    // The "Scope" property determines which parameter default value environment scopes appear as overridden/crossed out.
    // Tenant variable templates don't have scopes - only their values do, so we default to no scopes.
    // This causes all parameter default value environment scopes to appear crossed out,
    // even when a tenant variable may not actually apply in a particular environment.
    const tenantVariables = sourceItems.actionTemplates.map((at): ProjectSourceVariable => ({
        Name: at.Name,
        Type: getVariableTypeFromDisplaySettings(at.DisplaySettings["Octopus.ControlType"]),
        Scope: {},
    }));
    return [...processVariables, ...libraryVariables, ...tenantVariables];
};
export const getUniqueAccountVariableNames = (sourceItems: ProjectSourceItems): string[] => [
    ...new Set(getAllVariables(sourceItems)
        .filter((v) => isAccountVariableType(v.Type))
        .map((v) => v.Name)),
];
export const isOverrideVariableRow = (row: ParameterValueRow, platformHubAccounts: PlatformHubAccountSummary[]): boolean => {
    if (!row.Value) {
        return false;
    }
    //If it has a value, and it's not a platform hub account then it must be overridden with a variable.
    return !platformHubAccounts.find((a) => a.Id === row.Value?.Value);
};
export const createSummaryForParameterValue = (label: string, type: ControlType, options: Options, feeds: FeedResource[], environments: EnvironmentV2Resource[], workerPools: WorkerPoolResource[], teams: GetTeamsLookupBffResponseTeam[], channels: ChannelResource[], projects: ProjectSummaryResource[], packageParameterIsUsed?: boolean) => {
    // Override the createSummaryFor() function to handle package parameters a specific way
    return (value: PropertyValueResource | undefined) => {
        if (typeof value === "string" && type === ControlType.Package) {
            if (JsonUtils.tryParseJson(value)) {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
                const pkg = JSON.parse(value) as Partial<PackageReference<any>>;
                if (!pkg.PackageId) {
                    return Summary.placeholder(<div className={styles.noValue}>
                            <MissingVariablesIcon show={true}/>
                            <Text value={""} placeholder={"Provide value"} onChange={noOp} hideUnderline={true} monoSpacedFont={true}/>
                        </div>);
                }
                return createSummaryFor(label, type, options, feeds, environments, workerPools, teams, channels, projects, packageParameterIsUsed)(value);
            }
        }
        return createSummaryFor(label, type, options, feeds, environments, workerPools, teams, channels, projects, packageParameterIsUsed)(value);
    };
};
function getParameterValueRows(parameters: ProcessTemplateParameterResource[], action: StoredAction, sourceItems: ProjectSourceItems): ParameterValueRow[] {
    return parameters.flatMap((p, parameterIndex): ParameterValueRow[] => {
        let parameterWithOverrides = { ...p };
        let overrideVariableRow: ParameterValueRow | undefined = undefined;
        let startingValueIndex = 0;
        const controlType = p.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
        const overrideVariableName = action.Properties[p.Name];
        if (!!action.Properties[p.Name] && typeof overrideVariableName === "string" && isAccountControlType(controlType)) {
            overrideVariableRow = {
                Parameter: p,
                Value: { Value: overrideVariableName, Scope: {}, OverriddenScope: {} },
                ParameterIndex: parameterIndex,
                ValueIndex: startingValueIndex,
            };
            const completeOverrideVariableEnvironmentScope = getAllVariables(sourceItems)
                .filter((variable) => variable.Name === overrideVariableName)
                .flatMap((variable) => variable.Scope?.Environment?.filter((e) => e !== undefined) ?? []);
            parameterWithOverrides = {
                ...p,
                Values: p.Values.map((v) => ({
                    ...v,
                    OverriddenScope: { Environment: completeOverrideVariableEnvironmentScope },
                    OverrideValue: overrideVariableName,
                })),
            };
            startingValueIndex = 1;
        }
        else if (p.Values.length === 0) {
            return [{ Parameter: p, Value: null, ParameterIndex: parameterIndex, ValueIndex: 0 }];
        }
        return [
            ...(overrideVariableRow ? [overrideVariableRow] : []),
            ...parameterWithOverrides.Values.map<ParameterValueRow>((v, valueIndex) => ({ Parameter: parameterWithOverrides, Value: v, ParameterIndex: parameterIndex, ValueIndex: startingValueIndex + valueIndex })),
        ];
    });
}
interface ProcessTemplateConsumerParameterValueCellProps {
    data: ParameterValueRow;
    stepId: string;
    sourceItems: ProjectSourceItems;
    action: StoredAction;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    platformHubAccounts: PlatformHubAccountSummary[];
    focusedValueKey: TemplateValueKey | null;
    setFocusedValueKey: (key: TemplateValueKey | null) => void;
}
function ProcessTemplateConsumerParameterValueCell(props: ProcessTemplateConsumerParameterValueCellProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const valueKey = createTemplateValueKey(props.data.Parameter.Name, props.data.ValueIndex, "Value");
    const onFocus = () => props.setFocusedValueKey(valueKey);
    const onBlur = () => props.setFocusedValueKey(null);
    const controlType = props.data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
    const isFocused = props.focusedValueKey === valueKey;
    const rowCanBeFocused = !isAccountControlType(controlType) || isAccountControlTypeWithoutDefaultValues(props.data) || isOverrideVariableRow(props.data, props.platformHubAccounts);
    const rowIsFocused = isFocused && rowCanBeFocused;
    const hasValue = props.action.Properties[props.data.Parameter.Name] || props.data.Value;
    const getValue = () => {
        if (props.action.Properties[props.data.Parameter.Name] && (!isAccountControlType(controlType) || isAccountControlTypeWithoutDefaultValues(props.data))) {
            return props.action.Properties[props.data.Parameter.Name];
        }
        else if (props.data.Value) {
            return props.data.Value?.Value;
        }
        return undefined;
    };
    const tooltipContent = isOverrideVariableRow(props.data, props.platformHubAccounts) ? "You can only provide a single value" : "Override with a variable";
    const renderValue = () => {
        if (isAccountControlType(controlType) || controlType === ControlType.Sensitive) {
            return (<ReadonlyInlineVariableValue value={getValue()} variableType={getVariableTypeFromDisplaySettings(props.data.Parameter.DisplaySettings["Octopus.ControlType"])} defaultValue={undefined} accounts={getAccounts(props.platformHubAccounts, props.sourceItems)} certificates={{}} workerPools={{}} hasEditPermissions={rowCanBeFocused} sourceItems={undefined}/>);
        }
        const label = getLabel(props.data.Parameter);
        const options = selectOptionsToItems(props.data.Parameter.DisplaySettings["Octopus.SelectOptions"]);
        const packageParameterIsUsed = !!props.sourceItems.packages.items.find((p) => p.Properties["PackageParameterName"] === props.data.Parameter.Name);
        const value = props.action.Properties[props.data.Parameter.Name] || props.data.Value?.Value;
        const summary = createSummaryForParameterValue(label, controlType, options, props.sourceItems.packages.feeds, props.sourceItems.environments, props.sourceItems.workerPools.items, props.sourceItems.teams, props.sourceItems.channels, props.sourceItems.projects, packageParameterIsUsed)(value);
        return renderSummaryNodeForParameterValue(summary.node);
    };
    return (<div ref={containerRef}>
            {showMultipleValuesAndScopesIndicators(controlType, props.data) && (<div className={styles.multipleValuesAndScopesIndicator}>
                    <div>Multiple values</div>
                    {isAccountControlType(controlType) && (<div className={styles.overrideButtonContainer}>
                            <PopoverBasicWithTrigger description={<div className={styles.popoverBasicWithTriggerContainer} style={{ width: containerRef.current ? `${containerRef.current.offsetWidth - 24}px` : space[24] }}>
                                        <ProcessTemplateParameter key={props.data.Parameter.Name} stepId={props.stepId} parameter={props.data.Parameter} sourceItems={props.sourceItems} action={props.action} onParameterValueChanged={props.onParameterValueChanged} doBusyTask={props.doBusyTask} analyticsStepEditorDispatch={props.analyticsStepEditorDispatch} autoFocus={rowIsFocused}/>
                                    </div>} placement="top-end" width="extra-wide" triggerElement={<IconButtonWithTooltip toolTipContent={tooltipContent} customIcon={<PlusIcon size={20} color={isOverrideVariableRow(props.data, props.platformHubAccounts) ? themeTokens.color.icon.disabled : themeTokens.color.icon.secondary}/>} accessibleName={tooltipContent}/>} triggerAlignment="center" open={isOverrideVariableRow(props.data, props.platformHubAccounts) ? false : undefined} contentPadding={0}/>
                        </div>)}
                </div>)}
            <div onFocus={onFocus} onClick={onFocus} className={styles.valueContainer}>
                <PopoverWhenFocused isFocused={rowIsFocused} onClickOutside={onBlur} isContentCentered={true} isFullWidth={true}>
                    {rowIsFocused ? (<div className={styles.popoverWhenFocusedContainer}>
                            <ProcessTemplateParameter key={props.data.Parameter.Name} stepId={props.stepId} parameter={props.data.Parameter} sourceItems={props.sourceItems} action={props.action} onParameterValueChanged={props.onParameterValueChanged} doBusyTask={props.doBusyTask} analyticsStepEditorDispatch={props.analyticsStepEditorDispatch} autoFocus={rowIsFocused}/>
                        </div>) : hasValue ? (renderValue()) : (<div className={styles.noValue}>
                            {!props.data.Parameter.IsOptional && <MissingVariablesIcon show={true}/>}
                            <Text value={""} placeholder={props.data.Parameter.IsOptional ? "(Optional)" : "Provide value"} onChange={noOp} hideUnderline={true} monoSpacedFont={true}/>
                        </div>)}
                </PopoverWhenFocused>
            </div>
        </div>);
}
export const renderSummaryNodeForParameterValue = (node: React.ReactNode) => {
    if (typeof node === "string") {
        return <Text value={node.toString()} placeholder={node.toString()} onChange={noOp} hideUnderline={true} showValueAsTitleAttribute={true} monoSpacedFont={true}/>;
    }
    return <div className={styles.summaryNode}>{node}</div>;
};
export const getAccounts = (platformHubAccounts: PlatformHubAccountSummary[], sourceItems: ProjectSourceItems) => {
    const platformHubAccountIdToNameObject = platformHubAccounts.reduce((acc, a) => ({ ...acc, [a.Id]: a.Name }), {});
    const uniqueAccountVariableNames = getUniqueAccountVariableNames(sourceItems);
    const accountVariableNameToFormattedNameObject = uniqueAccountVariableNames.reduce((acc, name) => ({ ...acc, [name]: name }), {});
    return { ...platformHubAccountIdToNameObject, ...accountVariableNameToFormattedNameObject };
};
export const getControlTypeFromParameterValueRow = (row: ParameterValueRow): ControlType => row.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
export const isAccountControlTypeWithoutDefaultValues = (data: ParameterValueRow): boolean => {
    const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
    return isAccountControlType(controlType) && !data.Value;
};
export const showMultipleValuesAndScopesIndicators = (controlType: ControlType, currentRow: ParameterValueRow): boolean => isAccountControlType(controlType) && currentRow.ValueIndex === 0 && currentRow.Parameter.Values.length > 0;
export const defaultScopeValues: ScopeValues = {
    Actions: [],
    Channels: [],
    Environments: [],
    Machines: [],
    Roles: [],
    TenantTags: [],
    Processes: [],
};
export const templateParameterValueStyles = {
    calloutContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
    }),
    removedParameter: css({
        color: themeTokens.color.text.disabled,
    }),
    parameterNameCellPrimaryHeader: css({
        font: text.body.regular.small,
        fontWeight: fontWeight["700"],
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    parameterNameCellSecondaryHeader: css({
        font: text.code.regular.small,
        color: themeTokens.color.text.tertiary,
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    parameterHelpText: css({
        font: text.body.regular.small,
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    valueContainer: css({
        paddingLeft: space[12],
        paddingRight: space[12],
        width: "100%",
        height: `${baseCellHeightInRem}rem`,
        display: "flex",
        alignItems: "center",
    }),
    disabledText: css({
        color: themeTokens.color.text.disabled,
    }),
    popoverWhenFocusedContainer: css({
        paddingLeft: space[12],
        paddingRight: space[12],
        width: "100%",
    }),
    popoverBasicWithTriggerContainer: css({
        paddingLeft: space[12],
        paddingRight: space[12],
    }),
    noValue: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        gap: space[8],
    }),
    helpText: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    scopeContainer: css({
        height: `${baseCellHeightInRem}rem`,
        width: "100%",
        cursor: "default",
    }),
    multipleValuesAndScopesIndicator: css({
        height: `${multipleValuesAndScopesIndicatorHeightInRem}rem`,
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.tertiary,
        borderBottom: tableBorder,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingLeft: space[16],
        paddingRight: space[16],
    }),
    overrideButtonContainer: css({
        display: "flex",
        alignItems: "center",
    }),
    parameterContainer: css({
        paddingTop: space[16],
        paddingLeft: space[4],
        paddingRight: space[4],
    }),
    summaryNode: css({
        width: "100%",
        borderBottom: `${borderWidth[2]} solid transparent`,
        "&:hover": {
            borderBottom: `${borderWidth[2]} solid ${themeTokens.color.textField.border.hover}`,
        },
    }),
    accountParametersHeading: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: space[8],
    }),
};
const styles = templateParameterValueStyles;
